import React,{useState,useEffect} from 'react';
import {Link} from 'react-router-dom';
import {motion,AnimatePresence} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiArrowRight,FiCheck,FiPhone,FiCheckCircle,FiPackage,FiTool,FiAlertCircle}=FiIcons;

const ServiceDetail=({service})=> {
  const [isVideoLoaded,setIsVideoLoaded]=useState(false);

  const serviceMedia={
    'Rohbau': {type: 'video',src: 'https://canv.wasacon.com/rudt-bau/_assets/video/d15bd0722d69ec9507ccd5d15254038b.mp4'},
    'Klinkerarbeiten': {type: 'image',src: 'https://canv.wasacon.com/rudt-bau/_assets/media/0c87bfd41e772cb12be78235e6f7f53e.jpg'},
    'Putzarbeiten': {type: 'video',src: 'https://canv.wasacon.com/rudt-bau/_assets/video/097822a36f47d07143ede72d186f793a.mp4'},
    'Betonarbeiten': {type: 'video',src: 'https://canv.wasacon.com/rudt-bau/_assets/video/72625f99f6ee755b2ff4d9216c76f3b8.mp4'},
    'Maurerarbeiten': {type: 'video',src: 'https://canv.wasacon.com/rudt-bau/_assets/video/668b179f9a262e8560d069db74b21f2c.mp4'},
    'Pflasterverlegung': {type: 'image',src: 'https://canv.wasacon.com/rudt-bau/_assets/media/ae28306617446087eb526827f0f28121.jpg'}
  };

  const currentMedia=serviceMedia[service.title] || null;

  useEffect(()=> {
    setIsVideoLoaded(false);
  },[service.title]);

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-br from-gray-900 to-gray-800 text-white overflow-hidden">
        {currentMedia && (
          <div className="absolute inset-0">
            <AnimatePresence>
              {currentMedia.type==='video' ? (
                <motion.video
                  key={`${service.title}-video`}
                  initial={{opacity: 0}}
                  animate={{opacity: 0.5}}
                  exit={{opacity: 0}}
                  className="w-full h-full object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                  onLoadedData={()=> setIsVideoLoaded(true)}
                >
                  <source src={currentMedia.src} type="video/mp4" />
                </motion.video>
              ) : (
                <motion.img
                  key={`${service.title}-image`}
                  initial={{opacity: 0}}
                  animate={{opacity: 0.4}}
                  exit={{opacity: 0}}
                  src={currentMedia.src}
                  alt={`${service.title} Hintergrund`}
                  className="w-full h-full object-cover"
                />
              )}
            </AnimatePresence>
          </div>
        )}

        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/70"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0,y: 30}}
            animate={{opacity: 1,y: 0}}
            transition={{duration: 0.8}}
            className="text-center"
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-8 leading-tight">
              {service.title}
            </h1>
            <p className="text-2xl md:text-3xl mb-12 max-w-5xl mx-auto leading-relaxed font-light">
              {service.subtitle}
            </p>
            <Link
              to="/kontakt"
              className="inline-flex items-center space-x-3 bg-[#3466ad] text-white px-10 py-5 rounded-xl text-xl font-semibold hover:bg-[#2a5291] transition-all duration-300 shadow-2xl hover:shadow-3xl transform hover:-translate-y-1"
            >
              <span>Angebot anfordern</span>
              <SafeIcon icon={FiArrowRight} className="w-6 h-6" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Description */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{opacity: 0,x: -30}}
              whileInView={{opacity: 1,x: 0}}
              viewport={{once: true}}
              transition={{duration: 0.8}}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Professionelle {service.title}
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                {service.description}
              </p>
              <Link
                to="/kontakt"
                className="inline-flex items-center space-x-2 bg-[#3466ad] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#2a5291] transition-colors duration-200"
              >
                <span>Projekt besprechen</span>
                <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
              </Link>
            </motion.div>

            <motion.div
              initial={{opacity: 0,x: 30}}
              whileInView={{opacity: 1,x: 0}}
              viewport={{once: true}}
              transition={{duration: 0.8,delay: 0.2}}
              className="relative"
            >
              <img
                src={service.image}
                alt={service.title}
                className="w-full h-96 object-cover rounded-xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Klinker Detailed Content - Only for Klinkerarbeiten */}
      {service.id==='klinkerarbeiten' && service.detailedContent && (
        <>
          {/* Properties Section */}
          <section className="py-20 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{opacity: 0,y: 30}}
                whileInView={{opacity: 1,y: 0}}
                viewport={{once: true}}
                transition={{duration: 0.8}}
                className="max-w-4xl mx-auto"
              >
                <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg">
                  <div className="flex items-start space-x-4 mb-6">
                    <div className="w-12 h-12 bg-[#3466ad] rounded-xl flex items-center justify-center flex-shrink-0">
                      <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">
                        {service.detailedContent.properties.title}
                      </h3>
                      <p className="text-lg text-gray-600 leading-relaxed">
                        {service.detailedContent.properties.description}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* Delivery & Storage */}
          <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Delivery */}
                <motion.div
                  initial={{opacity: 0,x: -30}}
                  whileInView={{opacity: 1,x: 0}}
                  viewport={{once: true}}
                  transition={{duration: 0.8}}
                  className="bg-gray-50 rounded-2xl p-8 shadow-lg"
                >
                  <div className="flex items-start space-x-4 mb-6">
                    <div className="w-12 h-12 bg-[#3466ad] rounded-xl flex items-center justify-center flex-shrink-0">
                      <SafeIcon icon={FiPackage} className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">
                      {service.detailedContent.delivery.title}
                    </h3>
                  </div>
                  <ul className="space-y-4">
                    {service.detailedContent.delivery.points.map((point,index)=> (
                      <li key={index} className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-[#3466ad] rounded-full mt-2 flex-shrink-0"></div>
                        <p className="text-gray-600 leading-relaxed">{point}</p>
                      </li>
                    ))}
                  </ul>
                </motion.div>

                {/* Storage */}
                <motion.div
                  initial={{opacity: 0,x: 30}}
                  whileInView={{opacity: 1,x: 0}}
                  viewport={{once: true}}
                  transition={{duration: 0.8,delay: 0.2}}
                  className="bg-gray-50 rounded-2xl p-8 shadow-lg"
                >
                  <div className="flex items-start space-x-4 mb-6">
                    <div className="w-12 h-12 bg-[#3466ad] rounded-xl flex items-center justify-center flex-shrink-0">
                      <SafeIcon icon={FiAlertCircle} className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">
                      {service.detailedContent.storage.title}
                    </h3>
                  </div>
                  <p className="text-gray-600 leading-relaxed">
                    {service.detailedContent.storage.description}
                  </p>
                </motion.div>
              </div>
            </div>
          </section>

          {/* Processing */}
          <section className="py-20 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{opacity: 0,y: 30}}
                whileInView={{opacity: 1,y: 0}}
                viewport={{once: true}}
                transition={{duration: 0.8}}
                className="max-w-4xl mx-auto"
              >
                <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg">
                  <div className="flex items-start space-x-4 mb-6">
                    <div className="w-12 h-12 bg-[#3466ad] rounded-xl flex items-center justify-center flex-shrink-0">
                      <SafeIcon icon={FiTool} className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">
                        {service.detailedContent.processing.title}
                      </h3>
                      <p className="text-lg text-gray-600 leading-relaxed mb-6">
                        {service.detailedContent.processing.intro}
                      </p>
                    </div>
                  </div>
                  <div className="pl-16">
                    <h4 className="font-semibold text-gray-900 mb-4">Bei der Verarbeitung beachten:</h4>
                    <ul className="space-y-3">
                      {service.detailedContent.processing.conditions.map((condition,index)=> (
                        <li key={index} className="flex items-start space-x-3">
                          <SafeIcon icon={FiCheck} className="w-5 h-5 text-[#3466ad] mt-0.5 flex-shrink-0" />
                          <p className="text-gray-600 leading-relaxed">{condition}</p>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* Tips */}
          <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{opacity: 0,y: 30}}
                whileInView={{opacity: 1,y: 0}}
                viewport={{once: true}}
                transition={{duration: 0.8}}
                className="max-w-4xl mx-auto"
              >
                <div className="bg-gradient-to-br from-[#3466ad] to-[#2a5291] rounded-2xl p-8 md:p-12 text-white shadow-2xl">
                  <h3 className="text-2xl font-bold mb-6">
                    {service.detailedContent.tips.title}
                  </h3>
                  <div className="space-y-4">
                    {service.detailedContent.tips.points.map((tip,index)=> (
                      <div key={index} className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-white rounded-full mt-2 flex-shrink-0"></div>
                        <p className="text-white/90 leading-relaxed">{tip}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </section>
        </>
      )}

      {/* Features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0,y: 30}}
            whileInView={{opacity: 1,y: 0}}
            viewport={{once: true}}
            transition={{duration: 0.8}}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Unser Leistungsumfang
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Wir bieten Ihnen ein umfassendes Spektrum an {service.title.toLowerCase()} mit höchsten Qualitätsstandards und modernster Technik.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {service.features.map((feature,index)=> (
              <motion.div
                key={feature.title}
                initial={{opacity: 0,y: 30}}
                whileInView={{opacity: 1,y: 0}}
                viewport={{once: true}}
                transition={{duration: 0.8,delay: index * 0.1}}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#3466ad] rounded-lg flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiCheck} className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0,y: 30}}
            whileInView={{opacity: 1,y: 0}}
            viewport={{once: true}}
            transition={{duration: 0.8}}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Unser Vorgehen
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Strukturiert,professionell und transparent - so arbeiten wir für Ihren Erfolg und Ihre Zufriedenheit.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
            {service.process.map((step,index)=> (
              <motion.div
                key={step}
                initial={{opacity: 0,y: 30}}
                whileInView={{opacity: 1,y: 0}}
                viewport={{once: true}}
                transition={{duration: 0.8,delay: index * 0.2}}
                className="text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-[#3466ad] to-[#2a5291] rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-xl">{index + 1}</span>
                </div>
                <p className="text-gray-700 font-medium leading-relaxed">
                  {step}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#3466ad] to-[#2a5291] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{opacity: 0,y: 30}}
            whileInView={{opacity: 1,y: 0}}
            viewport={{once: true}}
            transition={{duration: 0.8}}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Bereit für Ihr {service.title}-Projekt?
            </h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto opacity-90">
              Kontaktieren Sie uns für eine unverbindliche Beratung und ein individuelles Angebot für Ihr Bauvorhaben.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/kontakt"
                className="inline-flex items-center space-x-2 bg-white text-[#3466ad] px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-colors duration-200"
              >
                <span>Unverbindliches Angebot anfordern</span>
                <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
              </Link>
              <a
                href="tel:+4924528607019"
                className="inline-flex items-center space-x-2 border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-[#3466ad] transition-colors duration-200"
              >
                <SafeIcon icon={FiPhone} className="w-5 h-5" />
                <span>02452 860 70 19</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;